import React, { useEffect, useState } from 'react';
import { clearPlans } from 'State/Plans';
import { getEntitlements } from 'State/User';
import CenterFrameLayout from 'Layouts/CenterFrameLayout';
import { useStore } from 'Hooks/useStore';
import { classCombine } from 'Utils/Utils';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { fetchMobileRegcode, getXid } from 'Api/ProfileApi';
import { setLocation } from 'Utils/Path';
import { useBeforeUnload, useLocation } from 'react-router-dom';
import styles from './Confirmation.module.css';
import { registerSecondScreen } from 'State/SecondScreen';
import { sessionStorageRemove } from 'Utils/Storage';

const DEVICES = {
  ROKU: {
    ALT: 'Roku',
    IMG: '/account/static/images/confirmation/Roku.svg',
    LINK: 'https://channelstore.roku.com/details/77604fb8acc15b958b633830cf903783/fox-nation',
    LINK_TEXT: 'Get the App',
  },
  APPLE_TV: {
    ALT: 'Apple TV',
    IMG: '/account/static/images/confirmation/AppleTV.svg',
    LINK: 'https://help.fox.com/s/article/How-to-Sign-in-to-your-Existing-Fox-Nation-Account-on-Apple-TV',
    LINK_TEXT: 'Read Tutorial',
  },
  FIRE_TV: {
    ALT: 'Fire TV',
    IMG: '/account/static/images/confirmation/FireTV.svg',
    LINK: 'https://www.amazon.com/Fox-News-Channel-Nation/dp/B07L5W9MVB',
    LINK_TEXT: 'Get the App',
  },
  SAMSUNG: {
    ALT: 'Samsung Smart TV',
    IMG: '/account/static/images/confirmation/Samsung.svg',
    LINK: 'https://help.fox.com/s/article/How-do-I-watch-FOX-Nation-on-Samsung-TV',
    LINK_TEXT: 'Read Tutorial',
  },
  CHROMECAST: {
    ALT: 'Google Chromecast',
    IMG: '/account/static/images/confirmation/Chromecast.svg',
    LINK: 'https://help.fox.com/s/article/How-do-I-watch-FOX-Nation-using-Chromecast',
    LINK_TEXT: 'How to Cast',
  },
  VIZIO: {
    ALT: 'Vizio',
    IMG: '/account/static/images/confirmation/Vizio.svg',
    LINK: 'https://help.fox.com/fox/s/article/How-do-I-watch-FOX-Nation-on-a-Vizio-Smart-TV',
    LINK_TEXT: 'App Preinstalled',
  },
  LG: {
    ALT: 'LG Smart TV',
    IMG: '/account/static/images/confirmation/LG.svg',
    LINK: 'https://help.fox.com/s/article/How-do-I-watch-FOX-Nation-on-LG-Smart-TV',
    LINK_TEXT: 'Read Tutorial',
  },
  ANDROID_TV: {
    ALT: 'Android TV',
    IMG: '/account/static/images/confirmation/AndroidTV.svg',
    LINK: 'https://play.google.com/store/apps/details?id=com.fng.foxnation&hl=en_US&gl=US&pli=1',
    LINK_TEXT: 'Get the App',
  },
  XBOX: {
    ALT: 'Xbox',
    IMG: '/account/static/images/confirmation/Xbox.svg',
    LINK: 'https://help.fox.com/s/article/How-do-I-watch-FOX-Nation-on-XBox-One',
    LINK_TEXT: 'Read Tutorial',
  },
};

const MobileLink = ({ device }) => {
  return (
    <a className={styles.watchLink} href={device.LINK} target="_blank" rel="noopener noreferrer">
      <img className={styles.watchImg} height="58.5%" width="100%" alt={device.ALT} src={device.IMG} />
    </a>
  );
};

const DesktopDevice = ({ device }) => {
  return (
    <div className={styles.deviceInfo}>
      <img width="73" height="31" alt={device.ALT} src={device.IMG} />
      <div className={styles.rightSide}>
        <a className={styles.deviceLink} href={device.LINK} target="_blank" rel="noopener noreferrer">
          {device.LINK_TEXT}
        </a>
      </div>
    </div>
  );
};

export function Confirmation({ customSubtitle, customMobileSubtitle }) {
  const { pathname = '' } = useLocation();
  const { hasEntitlements } = useStore('user');
  const { defaultConfig, routes } = useStore('appConfig');
  const { deviceIdCookieName } = defaultConfig;
  const { isChangePlan } = useStore('purchaseMode');
  const { isDeviceActivation } = useStore('secondScreen');

  const [isConnectedTV, setIsConnectedTV] = useState(true);
  const [regcode, setRegcode] = useState('');

  const isGiftConfirmation = pathname.includes(routes.giftCardRedeemConfirmation);

  const BUTTON_TEXT = 'Get Mobile App';

  useEffect(() => {
    const retrieveRegcode = async () => {
      const { xid } = await getXid();

      if (!xid) {
        console.error('Could not get XID');

        return;
      }

      const { code } = await fetchMobileRegcode(xid);
      setRegcode(code);
    };

    retrieveRegcode();
  }, [deviceIdCookieName]);

  const openApp = () => {
    trackEvent(
      TRACKING_ENUMS.EVENT_DEVICE_LINK_BUTTON_CLICKED,
      {
        page_item_title: 'signup confirmation',
        page_ui_element: 'button',
      },
      {
        item_title: 'signup confirmation',
        button_text: BUTTON_TEXT,
        ui_element: 'button',
      }
    );
    setLocation(`https://foxnation.onelink.me/CbbD/InstallApp?deep_link_value_sub1=${regcode}`, 'assign');
  };

  // Remove plan id from session storage, only needed during this screen for Heap integration
  // https://teamfox.atlassian.net/browse/NATCOM-1511
  useBeforeUnload(() => sessionStorageRemove('purchased_plan_id'));

  useEffect(() => {
    /* Clearing sign up plans so that if go to change plans it will fetch appropriate packages */
    getEntitlements(1);
    clearPlans();
  }, []);

  useEffect(() => {
    if (isDeviceActivation && hasEntitlements) {
      registerSecondScreen();
    }
  }, [isDeviceActivation, hasEntitlements]);

  const title = isGiftConfirmation
    ? 'Enjoy Your Fox Nation Gift'
    : isChangePlan
      ? 'Thank you for your order'
      : 'Thank you for joining Fox Nation';

  const subtitle =
    customSubtitle ||
    (isGiftConfirmation
      ? 'It may take up to one hour after you redeem your gift to be able to start watching shows on Fox Nation. ' +
        'You will receive an email confirming your gift subscription. If your gift includes merchandise,' +
        'another email will be sent when your merchandise ships.'
      : isChangePlan
        ? 'You will receive an email confirming the changes to your subscription plan'
        : 'You will receive an email confirming your subscription');

  const mobileTitle = isGiftConfirmation
    ? 'Enjoy Your Fox Nation Gift'
    : isChangePlan
      ? 'Thank you for your order'
      : 'Thank you!';

  const mobileSubtitle =
    customMobileSubtitle ||
    (isGiftConfirmation || !isChangePlan
      ? 'Go mobile with Fox Nation. Our mobile app makes it easy to watch anywhere!'
      : 'You will receive an email confirming the changes to your subscription plan');

  const showLoader = !hasEntitlements && !isGiftConfirmation;

  return (
    <CenterFrameLayout
      //Page titles indicate success of confirmation (for regular nongift purchases)
      //Don't want them to show before have entitlements
      title={!showLoader && title}
      subtitle={!showLoader && subtitle}
      mobileTitle={!showLoader && mobileTitle}
      mobileSubtitle={!showLoader && mobileSubtitle}
      customClass={styles.congrats}
      pageTitleClass={styles.pageTitle}
      pageSubTitleClass={styles.pageSubTitle}
      customFrameClass={styles.customFrame}
      showLoader={showLoader}
    >
      <div className={styles.mobileView}>
        <div className={styles.whereWatch}>
          <div className={styles.firstRow}>
            <MobileLink device={DEVICES.APPLE_TV} />
            <MobileLink device={DEVICES.ROKU} />
            <MobileLink device={DEVICES.FIRE_TV} />
          </div>
          <div className={styles.secondRow}>
            <div className={styles.SecRowFirstCol}>
              <MobileLink device={DEVICES.SAMSUNG} />
              <MobileLink device={DEVICES.CHROMECAST} />
            </div>
            <div className={styles.phoneContainer}>
              <img width="100%" height="98.725%" alt="Phone" src="/account/static/images/confirmation/phone.svg" />
              <img
                className={styles.wifiImg}
                width="67%"
                height="22.328%"
                alt="WiFi Connect"
                src="/account/static/images/confirmation/wifi.svg"
              />
              <span className={styles.phoneCopy}>Connect to your TV</span>
            </div>
            <div className={styles.SecRowLastCol}>
              <MobileLink device={DEVICES.VIZIO} />
              <MobileLink device={DEVICES.LG} />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.desktopView}>
        <div className={styles.title}>Watch your favorite shows, documentaries, and movies anywhere with our app</div>
        <div className={styles.subtitle}>Choose your device below to see how to watch</div>
        <div className={styles.tabContainer}>
          <button
            onClick={() => setIsConnectedTV(true)}
            className={classCombine(styles.tab, isConnectedTV && styles.selected)}
          >
            Connected TV
          </button>
          <button
            onClick={() => setIsConnectedTV(false)}
            className={classCombine(styles.tab, !isConnectedTV && styles.selected)}
          >
            Mobile & Tablet
          </button>
        </div>
        <div className={styles.line}></div>
        {isConnectedTV ? (
          <div className={styles.connectedContainer}>
            <div className={styles.desktopCol}>
              <span className={styles.colTitle}>Add Directly to Your Device:</span>
              <DesktopDevice device={DEVICES.ROKU} />
              <DesktopDevice device={DEVICES.FIRE_TV} />
              <DesktopDevice device={DEVICES.ANDROID_TV} />
              <DesktopDevice device={DEVICES.VIZIO} />
            </div>
            <div className={styles.desktopCol}>
              <span className={styles.colTitle}>How to Download to:</span>
              <DesktopDevice device={DEVICES.APPLE_TV} />
              <DesktopDevice device={DEVICES.SAMSUNG} />
              <DesktopDevice device={DEVICES.LG} />
              <DesktopDevice device={DEVICES.XBOX} />
              <DesktopDevice device={DEVICES.CHROMECAST} />
            </div>
          </div>
        ) : (
          <button className={styles.devicesButton} type="button" onClick={openApp}>
            <div className={styles.mobileDevice}>
              <img
                alt="iOS Devices"
                height="124"
                width="105"
                src="/account/static/images/confirmation/iOSDevices.svg"
              />
              <span className={styles.mobileName}>iOS</span>
            </div>
            <div className={styles.mobileDevice}>
              <img
                alt="Android Devices"
                height="124"
                width="105"
                src="/account/static/images/confirmation/AndroidDevices.svg"
              />
              <span className={styles.mobileName}>Android</span>
            </div>
          </button>
        )}
      </div>
      <div className={styles.buttons}>
        <button className={styles.getAppButton} type="button" onClick={openApp}>
          {BUTTON_TEXT}
        </button>
        <a className={styles.watchOnline} href={defaultConfig.rootDomain}>
          {isGiftConfirmation ? 'Continue Home' : 'Watch Online Now'}
        </a>
      </div>
    </CenterFrameLayout>
  );
}
